/**
 * ScreenReaderAnnouncer — ARIA live region for dynamic announcements.
 *
 * Story 1.8 AC#3. Batches rapid updates (>1/second).
 *
 * Usage:
 *   <ScreenReaderAnnouncer message="3 tables completed" />
 *   <ScreenReaderAnnouncer message="Data Readiness Score updated to 78, Good" level="assertive" />
 */

import { useEffect, useRef, useState } from "react";

export interface ScreenReaderAnnouncerProps {
  message: string;
  level?: "polite" | "assertive";
  /** Debounce interval in ms for rapid updates (default 1000) */
  debounceMs?: number;
}

export function ScreenReaderAnnouncer({
  message,
  level = "polite",
  debounceMs = 1000,
}: ScreenReaderAnnouncerProps) {
  const [announced, setAnnounced] = useState("");
  // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!message) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setAnnounced(message);
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [message, debounceMs]);

  return (
    <div role="status" aria-live={level} aria-atomic="true" className="sr-only">
      {announced}
    </div>
  );
}
